import { Component, HostListener, OnDestroy, OnInit, inject } from '@angular/core'
import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular'
import { InteractionStatus, RedirectRequest, EventMessage, EventType, AccountInfo } from '@azure/msal-browser'
import { filter, takeUntil } from 'rxjs/operators'
import { Subject } from 'rxjs'
import { environment } from 'src/environments/environment'
import { NotificationService } from '../../services/notification.service'
import { UtilsService } from '../../services/utils.service'
import { AuthService } from '../../services/auth.service'
import { Router } from '@angular/router'
import { MatButton } from '@angular/material/button';
import { SpinnerComponent } from '../spinner/spinner.component';

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    imports: [MatButton, SpinnerComponent]
})
export class LoginComponent implements OnInit, OnDestroy {
  private msalGuardConfig = inject<MsalGuardConfiguration>(MSAL_GUARD_CONFIG);
  private msalBroadcastService = inject(MsalBroadcastService);
  private msalService = inject(MsalService);
  private auth = inject(AuthService);
  private notiService = inject(NotificationService);
  private utils = inject(UtilsService);
  private router = inject(Router);

  isHttps = true
  showLoginButton = false
  containerHeight = window.innerHeight - 100
  private readonly _destroying$ = new Subject<void>()
  showRefreshButton = false
  isAlive = true

  @HostListener('window:resize') onResize() {
    setTimeout(() => {
      this.containerHeight = window.innerHeight - 100
    }, 1000) // wait for iPhone
  }

  constructor() {
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS)
      )
      .subscribe((result: EventMessage) => {
        console.log(result);
        const payload = result.payload as AccountInfo;
        this.msalService.instance.setActiveAccount(payload);
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        if (this.msalService.instance.getAllAccounts().length > 0) {
          this.setLoggedIn();
        }
      });
  }

  ngOnInit() {
    // will get cryptic msal errors if trying to login from http
    if (!this.utils.isHttps()) {
      this.isHttps = false
    }

    this.msalService.handleRedirectObservable().subscribe();
    // button stays hidden until MSAL is idle (inProgress$ === None below)

    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.LOGOUT_START
        )
      )
      .subscribe(() => {
        if (this.msalService.instance.getAllAccounts().length === 0) {
          window.location.pathname = '/';
        } else {
          this.setLoginDisplay();
        }
      });

    this.msalBroadcastService.inProgress$
      .pipe(
        filter(
          (status: InteractionStatus) => status === InteractionStatus.None
        ),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.setLoginDisplay();
        this.checkAndSetActiveAccount();
      });
  }

  setLoginDisplay() {
    this.showLoginButton = this.msalService.instance.getAllAccounts().length <= 0;
  }

  checkAndSetActiveAccount() {
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     * Note: Basic usage demonstrated. Your app may require more complicated account selection logic
     */
    const activeAccount = this.msalService.instance.getActiveAccount();

    if (!activeAccount && this.msalService.instance.getAllAccounts().length > 0) {
      const accounts = this.msalService.instance.getAllAccounts();
      this.msalService.instance.setActiveAccount(accounts[0]);
    }
  }

  loginClicked() {
    this.showLoginButton = false
    const request = this.msalGuardConfig.authRequest
      ? { ...this.msalGuardConfig.authRequest } as RedirectRequest
      : undefined;

    this.msalService.loginRedirect(request).subscribe({
      error: (err) => {
        console.error('loginRedirect failed', err);
        this.showLoginButton = true;
        this.showRefreshButton = true;
      }
    });
  }

  goToHttps() {
    window.location.href = environment.applicationUrl;
  }

  ngOnDestroy() {
    this._destroying$.next(undefined)
    this._destroying$.complete()
    this.isAlive = false
  }

  refresh(): void {
    window.location.reload()
  }

  clearCache() {
    localStorage.clear()
    this.notiService.showInfo('Cache cleared. Logging out...')
    setTimeout(() => {
      this.auth.signOut()
    }, 2000)
  }

  setLoggedIn() {
    const accounts = this.msalService.instance.getAllAccounts();
    const email = accounts[0]?.idTokenClaims?.emails
      ? accounts[0].idTokenClaims.emails?.[0] 
      : accounts[0]?.idTokenClaims?.email;
    const user = { email: email };
    this.auth.setCurrentUser(user);
    this.router.navigateByUrl('home');
  }
}
